# Route: /admin/reason-vocabularies/:id
# View a reason vocabulary + its reason entries, with lifecycle actions
# (make-effective, deprecate) accessible via the overflow menu.
# Permission: ReasonVocabulary:read (view) + ReasonVocabulary:approve (lifecycle)
# Feature: F53a G8-b (vocabulary admin — detail)

# signature_surface: the header's approve applies a signature (the approver is recorded on the
# version), and the approver confirms the vocabulary this page renders — its info and its
# entries. So nothing this page mounts is a customization target (Feature #1913). The shell
# around it is the layout's, and stays one.
page-reason-vocabulary-detail:
  type: Page
  archetype: detail
  signature_surface: true
  meta:
    title: $t.admin.reasons.detail.meta_title
    route: /admin/reason-vocabularies/:id
    layout: shell
    auth: required
  data:
    vocabulary:
      model: ReasonVocabulary
      key: $route.id
      required: true
    # F92 FR-4 — version-history rows for this logical vocabulary, keyed on
    # the SAME $route.id as `vocabulary` above (the emitter maps a single
    # positional :id placeholder — see data-provider.cir.yaml's
    # ReasonVocabularyVersions entry).
    versions:
      model: ReasonVocabularyVersions[]
      key: $route.id
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - reason-detail-header
    - reason-detail-approval-note
    - reason-detail-info
    - reason-detail-entries
    - reason-version-history

# ── Header ───────────────────────────────────────────────────────────────────
# Lifecycle actions live in the ⋯ overflow menu per the design-system rule
# "lifecycle and destructive actions hide in a ⋯ overflow menu" (XP85-UX-003).
#
# F92 FR-14 — each lifecycle action's visibility binds to a server-computed
# transition flag (vocabulary.flags.can*), never a hardcoded status, so any D10
# lifecycle profile renders correctly with no CIR change. The flags come from the
# detail DTO (models.cir.yaml ReasonVocabulary.flags / VersionTransitionFlags);
# `visible_when` on an overflow item is honoured by the emitter's reactive guard,
# and the server still enforces the transition (an invalid one returns a 4xx that
# xp85-action-complete surfaces as an error toast).
#
# The flags answer for the caller as well as for the version: each is false for
# a caller who does not hold the permission its route requires
# (VersionTransitionFlags, computed from CallerVersionGrants), so an Operator
# holding only :update is offered none of the four approve-gated items.
#
# Each item also carries an explicit `permission:` (MenuItem.Permission)
# naming the permission the API route requires (ReasonVocabularyEndpoints.cs):
# submit_review on ReasonVocabulary:update, new_version on :create, and
# approve/return_to_draft/make_effective/deprecate on :approve. It is the
# client-side statement of the same rule the flag already carries — the
# permission guard is innermost, visible_when outermost (MenuItem.cs).
#
# Immutability of Effective/Deprecated entries is enforced by the API
# (ReasonVocabularyEndpoints.Update rejects non-Draft), so the UI does not need
# to gate the inline editor explicitly — attempted edits return 409 from the
# server, which the form surfaces as a toast.

reason-detail-header:
  type: PageHeader
  back_route: /admin/reason-vocabularies
  title_prefix: $t.admin.reasons.detail.title_prefix
  bind:
    content: vocabulary.displayName
  # F53a UX M-03: ReasonVocabulary is versioned regulated master data, so per
  # XP85-COMPLIANCE-002 the detail page must expose an audit-trail link.
  actions:
    - label: $t.admin.reasons.detail.btn_edit
      route: /admin/reason-vocabularies/:id/edit
      params:
        id: $data.vocabulary.id
      return_context: origin_only
      color: info
      # Content is mutable only in Draft (the server refuses an update to any
      # later status), so Edit follows the same server-computed flag the
      # lifecycle actions use rather than being offered on a frozen version.
      visible_when: vocabulary.flags.canEdit
      permission: "ReasonVocabulary:update"
    # F79 — canonical /audit (was the stale /admin/audit, no-lasagna L2). Forward
    # channel seeds the audit filter bar; return channel gives /audit a contextual
    # Back to this detail.
    - label: $t.admin.reasons.detail.btn_audit
      route: /audit
      query:
        entityType: ReasonVocabulary
        entityId: $data.vocabulary.id
      return_context: origin_only
      variant: ghost
  overflow:
    - label: $t.admin.reasons.detail.btn_submit_review
      color: info
      visible_when: vocabulary.flags.canSubmitForReview
      permission: "ReasonVocabulary:update"
      action:
        type: invoke
        model: ReasonVocabulary
        operation: submit_review
    - label: $t.admin.reasons.detail.btn_approve
      color: go
      visible_when: vocabulary.flags.canApprove
      permission: "ReasonVocabulary:approve"
      action:
        type: invoke
        model: ReasonVocabulary
        operation: approve
    - label: $t.admin.reasons.detail.btn_return_to_draft
      color: info
      visible_when: vocabulary.flags.canReturnToDraft
      permission: "ReasonVocabulary:approve"
      action:
        type: invoke
        model: ReasonVocabulary
        operation: return_to_draft
    - label: $t.admin.reasons.detail.btn_make_effective
      color: go
      visible_when: vocabulary.flags.canMakeEffective
      permission: "ReasonVocabulary:approve"
      action:
        type: invoke
        model: ReasonVocabulary
        operation: make_effective
    # F92 M-5 — success copy: the "on_success/toast" mechanism (ActionSpec.
    # SuccessMessage, entry-sign.cir.yaml's `success_message:`) is threaded
    # into the dispatched event ONLY by FormEmitter (XP85.Emitter.Lit/Forms/
    # FormEmitter.cs) — MenuItem (this overflow item's type) has no
    # SuccessMessage field (XP85.Core/Types/MenuItem.cs) and
    # RowMenuEmitter.GenerateMenuItemActionCode's "invoke" case dispatches
    # `xp85-action` with no successMessage key, so a post-success toast
    # cannot be wired from CIR YAML alone today. The `confirm:` dialog IS
    # fully wired for overflow items (MenuItem.Confirm), so the wayfinding
    # copy is surfaced there instead — the operator reads it before
    # dispatch, telling them where the new Draft will appear.
    - label: $t.admin.reasons.detail.btn_new_version
      color: info
      visible_when: vocabulary.flags.canCreateNewVersion
      permission: "ReasonVocabulary:create"
      action:
        type: invoke
        model: ReasonVocabulary
        operation: new_version
        confirm: $t.admin.reasons.detail.btn_new_version_confirm
    - label: $t.admin.reasons.detail.btn_deprecate
      color: caution
      visible_when: vocabulary.flags.canDeprecate
      permission: "ReasonVocabulary:approve"
      action:
        type: invoke
        model: ReasonVocabulary
        operation: deprecate
        confirm: $t.admin.reasons.detail.btn_deprecate_confirm

# ── Info card ────────────────────────────────────────────────────────────────

# J2 — the version's creator is not offered Approve; this says why, instead of leaving them to
# suspect a missing permission. Bound to the server's own reading (flags.approvalNeedsAnotherPerson),
# which is true only when the version awaits an approval the caller is excluded from giving.
reason-detail-approval-note:
  type: Text
  content: $t.admin.reasons.detail.approval_needs_another_person
  visible_when: vocabulary.flags.approvalNeedsAnotherPerson
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

reason-detail-info:
  type: Card
  spatial:
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - reason-info-grid

reason-info-grid:
  type: Grid
  spatial:
    columns: label-value
    gap: $spacing.md
    responsive:
      sm:
        columns: 1
  children:
    - reason-info-name-label
    - reason-info-name-value
    - reason-info-display-name-label
    - reason-info-display-name-value
    - reason-info-description-label
    - reason-info-description-value
    - reason-info-status-label
    - reason-info-status-value
    - reason-info-version-label
    - reason-info-version-value

reason-info-name-label:
  type: Text
  content: $t.admin.reasons.detail.label_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

reason-info-name-value:
  type: Text
  bind:
    content: vocabulary.name
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono

reason-info-display-name-label:
  type: Text
  content: $t.admin.reasons.detail.label_display_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

reason-info-display-name-value:
  type: Text
  bind:
    content: vocabulary.displayName
  tokens:
    font_size: $font.size.md

reason-info-description-label:
  type: Text
  content: $t.admin.reasons.detail.label_description
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

reason-info-description-value:
  type: Text
  bind:
    content: vocabulary.description
  tokens:
    font_size: $font.size.md

reason-info-status-label:
  type: Text
  content: $t.admin.reasons.detail.label_status
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

# F92 — five-state variant chain (Draft/InReview/Approved/Effective/Obsolete).
# Effective is the sole "go" (the one live version); Approved renders info
# (frozen + ready, but not yet the live one) rather than go, so it stays
# visually distinct from Effective; Obsolete (withdrawn, terminal) is
# caution; Draft/InReview fall through to the final else (info).
reason-info-status-value:
  type: StatusBadge
  bind:
    content: vocabulary.versionStatus
    variant:
      when: "vocabulary.versionStatus == 'Effective'"
      value: go
      else:
        when: "vocabulary.versionStatus == 'Approved'"
        value: info
        else:
          when: "vocabulary.versionStatus == 'Obsolete'"
          value: caution
          else: info

reason-info-version-label:
  type: Text
  content: $t.admin.reasons.detail.label_version
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

reason-info-version-value:
  type: Text
  bind:
    content: vocabulary.versionNumber
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono

# ── Reason entries table ─────────────────────────────────────────────────────

reason-detail-entries:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - reason-entries-title
    - reason-entries-table

reason-entries-title:
  type: Text
  content: $t.admin.reasons.detail.entries_title
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

reason-entries-table:
  type: DataTable
  bind:
    rows: vocabulary.entries
  empty: reason-entries-empty
  columns:
    - key: code
      label: $t.admin.reasons.detail.col_code
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: displayName
      label: $t.admin.reasons.detail.col_display_name
      tokens:
        font_size: $font.size.sm
    - key: description
      priority: low
      label: $t.admin.reasons.detail.col_description
      width: 1fr
      tokens:
        font_size: $font.size.sm
    - key: category
      priority: medium
      label: $t.admin.reasons.detail.col_category
      tokens:
        font_size: $font.size.sm
    - key: selectable
      label: $t.admin.reasons.detail.col_selectable
      tokens:
        font_size: $font.size.sm
    - key: supplementRequired
      label: $t.admin.reasons.detail.col_supplement
      tokens:
        font_size: $font.size.sm
  a11y:
    label: $t.admin.reasons.detail.entries_table_label
  tokens:
    border: $border.default
    radius: $radius.md

reason-entries-empty:
  type: EmptyState
  content: $t.admin.reasons.detail.entries_empty
  body: $t.admin.reasons.detail.entries_empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.md

# ── Version history ──────────────────────────────────────────────────────────
# F92 FR-4 — every version sharing this vocabulary's logical name, ordered by
# creation. Each row links to that version's own detail page (row.id), so an
# operator can navigate the full Draft -> ... -> Obsolete chain.

reason-version-history:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - reason-version-history-title
    - reason-version-history-table

reason-version-history-title:
  type: Text
  content: $t.admin.reasons.detail.card_versions_title
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

reason-version-history-table:
  type: DataTable
  bind:
    rows: versions
  empty: reason-version-history-empty
  row_action:
    type: navigate
    route: /admin/reason-vocabularies/:id
    params:
      id: $row.id
  columns:
    - key: versionNumber
      label: $t.admin.reasons.detail.col_version_number
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: versionStatus
      label: $t.admin.reasons.detail.col_version_status
      display: badge
      variant:
        when: "$row.versionStatus == 'Effective'"
        value: go
        else:
          when: "$row.versionStatus == 'Approved'"
          value: info
          else:
            when: "$row.versionStatus == 'Obsolete'"
            value: caution
            else: info
    - key: createdAt
      label: $t.admin.reasons.detail.col_version_created_at
      format: compliance-datetime
      tokens:
        font_size: $font.size.sm
    # F92 M-4 — createdBy is resolved to a human display name server-side by
    # GetVersionHistoryHandlerBase (via the batched IAuditEnrichmentLookup, the
    # same resolver the audit + tracking read models use), falling back to the
    # raw write-time subject only when no profile resolves. So the column binds
    # createdBy directly and carries no `font_family: mono` (raw-id convention).
    - key: createdBy
      label: $t.admin.reasons.detail.col_version_created_by
      tokens:
        font_size: $font.size.sm
    - key: effectiveFrom
      label: $t.admin.reasons.detail.col_version_effective_from
      format: compliance-datetime
      tokens:
        font_size: $font.size.sm
  a11y:
    label: $t.admin.reasons.detail.versions_table_label
  tokens:
    border: $border.default
    radius: $radius.md

reason-version-history-empty:
  type: EmptyState
  content: $t.admin.reasons.detail.versions_empty
  body: $t.admin.reasons.detail.versions_empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.md
